// ============================================================
// frontend/src/views/AdminDashboard.tsx
// ============================================================
// Super-admin landing page. Shows:
//   - Stat cards (pending / active / suspended / total clients & sessions)
//   - Tabbed list of therapists with actions per status:
//       pending   → Approve / Reject
//       active    → Suspend / Delete
//       rejected  → Re-approve (back to active) / Delete
//       suspended → Reactivate / Delete
//
// Mounted at the top of App.tsx when the logged-in user has role
// 'super_admin', completely replacing the therapist view.
// ============================================================

import { useCallback, useEffect, useState } from 'react';
import {
  CheckCircle2, XCircle, PauseCircle, PlayCircle, Trash2,
  LogOut, Sun, Moon, UserPlus, Copy, RefreshCw, Check,
  Clock, Users, UserX, Ban, Briefcase, CalendarDays,
  Mail, KeyRound,
} from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useTheme } from '../lib/theme';
import { Modal, ModalFooter, Field } from '../components/UI';
import { ConsiliumLoader } from '../components/ConsiliumLoader';
import type {
  AdminTherapistListResponse, AdminTherapistListItem,
  AdminStatsResponse, TherapistStatus,
  AdminCreateTherapistResponse,
} from '@consilium/shared';

type Tab = 'pending' | 'active' | 'rejected' | 'suspended';

const TAB_LABELS: Record<Tab, string> = {
  pending: 'Pending',
  active: 'Active',
  rejected: 'Rejected',
  suspended: 'Suspended',
};

const STATUS_STYLE: Record<TherapistStatus, { dot: string; bg: string; color: string; text: string }> = {
  pending:   { dot: '#e9c97a', bg: 'rgba(214, 174, 89, 0.12)',  color: '#e9c97a', text: 'Pending'   },
  active:    { dot: '#a7d6b3', bg: 'rgba(115, 168, 130, 0.12)', color: '#a7d6b3', text: 'Active'    },
  rejected:  { dot: '#f0c1b5', bg: 'rgba(201, 122, 107, 0.12)', color: '#f0c1b5', text: 'Rejected'  },
  suspended: { dot: '#bdbdbd', bg: 'rgba(140, 140, 140, 0.12)', color: '#bdbdbd', text: 'Suspended' },
};

// Stable, pleasant avatar tint derived from the therapist id.
function avatarTint(id: string): string {
  const palette = ['#d4a574', '#84a98c', '#c97a6b', '#a8a29e', '#b8895c', '#7a9eb1', '#c9a87a'];
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return palette[Math.abs(h) % palette.length];
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function AdminDashboard() {
  const { therapist, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();

  const [tab, setTab] = useState<Tab>('pending');
  const [stats, setStats] = useState<AdminStatsResponse | null>(null);
  const [rows, setRows] = useState<AdminTherapistListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  // Confirmation/reason modal for reject / suspend / delete
  const [modal, setModal] = useState<
    | null
    | { kind: 'reject';   target: AdminTherapistListItem }
    | { kind: 'suspend';  target: AdminTherapistListItem }
    | { kind: 'delete';   target: AdminTherapistListItem }
  >(null);
  const [modalReason, setModalReason] = useState('');

  // Edit-email modal state
  const [emailTarget, setEmailTarget] = useState<AdminTherapistListItem | null>(null);
  const [emailDraft, setEmailDraft] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailSaving, setEmailSaving] = useState(false);

  // Reset-password modal: form first, then success view showing the
  // temp password once. Same shape as create-user success.
  const [resetTarget, setResetTarget] = useState<AdminTherapistListItem | null>(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetSaving, setResetSaving] = useState(false);
  const [resetDone, setResetDone] = useState(false);
  const [resetCopied, setResetCopied] = useState(false);

  // Create-user flow has two screens in one modal: a form, then a
  // success view that shows the temp password once (admin must copy
  // it — the server never echoes it back later).
  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createEmail, setCreateEmail] = useState('');
  const [createPassword, setCreatePassword] = useState('');
  const [createError, setCreateError] = useState('');
  const [creating, setCreating] = useState(false);
  const [createdRow, setCreatedRow] = useState<AdminTherapistListItem | null>(null);
  // The temp password we just submitted — held only in this component's
  // memory so the success screen can display it. Cleared on close.
  const [createdTempPassword, setCreatedTempPassword] = useState('');
  const [copied, setCopied] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setErrorMsg('');
    try {
      const [statsRes, listRes] = await Promise.all([
        api.get<AdminStatsResponse>('/admin/stats'),
        api.get<AdminTherapistListResponse>(`/admin/therapists?status=${tab}`),
      ]);
      setStats(statsRes);
      setRows(listRes.therapists);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { refresh(); }, [refresh]);

  const runAction = async (
    therapistId: string,
    action: 'approve' | 'reject' | 'suspend' | 'reactivate' | 'delete',
    reason?: string,
  ) => {
    setBusyId(therapistId);
    setErrorMsg('');
    try {
      // 'reactivate' is just /approve — the server handles status transition.
      if (action === 'delete') {
        await api.del(`/admin/therapists/${therapistId}`);
      } else if (action === 'approve' || action === 'reactivate') {
        await api.post(`/admin/therapists/${therapistId}/approve`);
      } else if (action === 'reject') {
        await api.post(`/admin/therapists/${therapistId}/reject`, { reason });
      } else if (action === 'suspend') {
        await api.post(`/admin/therapists/${therapistId}/suspend`, { reason });
      }
      await refresh();
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setBusyId(null);
      setModal(null);
      setModalReason('');
    }
  };

  const submitModal = () => {
    if (!modal) return;
    if (modal.kind === 'delete') {
      runAction(modal.target.id, 'delete');
    } else {
      runAction(modal.target.id, modal.kind, modalReason.trim() || undefined);
    }
  };

  // ---------- Edit-email helpers ----------

  const openEditEmail = (t: AdminTherapistListItem) => {
    setEmailTarget(t);
    setEmailDraft(t.email);
    setEmailError('');
  };
  const closeEditEmail = () => {
    setEmailTarget(null);
    setEmailDraft('');
    setEmailError('');
  };
  const submitEditEmail = async () => {
    if (!emailTarget) return;
    const next = emailDraft.trim();
    if (!next) { setEmailError('Email is required.'); return; }
    if (next.toLowerCase() === emailTarget.email) { closeEditEmail(); return; }
    setEmailSaving(true);
    setEmailError('');
    try {
      await api.put(`/admin/therapists/${emailTarget.id}/email`, { email: next });
      await refresh();
      closeEditEmail();
    } catch (err) {
      setEmailError(err instanceof Error ? err.message : 'Failed to update email');
    } finally {
      setEmailSaving(false);
    }
  };

  // ---------- Reset-password helpers ----------

  const openResetPassword = (t: AdminTherapistListItem) => {
    setResetTarget(t);
    setResetPassword(generateTempPassword());
    setResetError('');
    setResetDone(false);
    setResetCopied(false);
  };
  const closeResetPassword = () => {
    setResetTarget(null);
    setResetPassword('');
    setResetError('');
    setResetDone(false);
    setResetCopied(false);
  };
  const submitResetPassword = async () => {
    if (!resetTarget) return;
    if (!resetPassword || resetPassword.length < 8) {
      setResetError('Temp password must be at least 8 characters.');
      return;
    }
    setResetSaving(true);
    setResetError('');
    try {
      await api.post(`/admin/therapists/${resetTarget.id}/reset-password`, {
        tempPassword: resetPassword,
      });
      setResetDone(true);
    } catch (err) {
      setResetError(err instanceof Error ? err.message : 'Failed to reset password');
    } finally {
      setResetSaving(false);
    }
  };
  const copyResetCredentials = async () => {
    if (!resetTarget) return;
    const text = `Email: ${resetTarget.email}\nTemporary password: ${resetPassword}`;
    try {
      await navigator.clipboard.writeText(text);
      setResetCopied(true);
      setTimeout(() => setResetCopied(false), 2000);
    } catch { /* clipboard unavailable — password is visible */ }
  };

  // ---------- Create-user helpers ----------

  const openCreate = () => {
    setCreateOpen(true);
    setCreateName('');
    setCreateEmail('');
    setCreatePassword(generateTempPassword());
    setCreateError('');
    setCreatedRow(null);
    setCreatedTempPassword('');
    setCopied(false);
  };

  const closeCreate = () => {
    setCreateOpen(false);
    setCreateName('');
    setCreateEmail('');
    setCreatePassword('');
    setCreateError('');
    setCreatedRow(null);
    setCreatedTempPassword('');
    setCopied(false);
  };

  const submitCreate = async () => {
    setCreateError('');
    if (!createName.trim() || !createEmail.trim() || !createPassword) {
      setCreateError('Name, email, and temp password are required.');
      return;
    }
    if (createPassword.length < 8) {
      setCreateError('Temp password must be at least 8 characters.');
      return;
    }
    setCreating(true);
    try {
      const res = await api.post<AdminCreateTherapistResponse>('/admin/therapists', {
        name: createName.trim(),
        email: createEmail.trim(),
        tempPassword: createPassword,
      });
      setCreatedRow(res.therapist);
      setCreatedTempPassword(createPassword);
      await refresh();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const copyCredentials = async () => {
    if (!createdRow) return;
    const text = `Email: ${createdRow.email}\nTemporary password: ${createdTempPassword}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API can fail (no permission, insecure context). Fall
      // back silently — the password is still visible on screen.
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>
      {/* Custom admin top bar — distinct from the therapist Header. */}
      <header className="top-bar">
        <div style={{
          maxWidth: 1100, margin: '0 auto',
          padding: '16px 24px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <div className="display" style={{ fontSize: 22, fontWeight: 600, letterSpacing: '-0.025em' }}>
              Consilium
            </div>
            <div className="mono-cap">Admin</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 13, color: 'var(--muted)', marginRight: 4 }}>
              {therapist?.email}
            </span>
            <button onClick={toggleTheme} className="btn-icon" aria-label="Toggle theme">
              {theme === 'dark' ? <Sun size={17} /> : <Moon size={17} />}
            </button>
            <button onClick={() => logout()} className="btn-icon" title="Sign out" aria-label="Sign out">
              <LogOut size={17} />
            </button>
          </div>
        </div>
      </header>

      <main
        style={{
          maxWidth: 1100, margin: '0 auto',
          padding: '28px 24px 48px',
        }}
      >
        {/* Page heading */}
        <div style={{ marginBottom: 24 }}>
          <h1 className="display" style={{ fontSize: 32, fontWeight: 600, letterSpacing: '-0.03em', margin: 0 }}>
            Therapist accounts
          </h1>
          <div style={{ fontSize: 14, color: 'var(--muted)', marginTop: 6 }}>
            Approve, suspend, or remove therapists. All practice data is fully isolated per account.
          </div>
        </div>

        {/* Stats */}
        <section style={{ marginBottom: 28 }}>
          <div style={{
            display: 'grid', gap: 12,
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          }}>
            <StatCard
              label="Pending"
              value={stats?.pending ?? '—'}
              icon={<Clock size={16} />}
              tint="rgba(214, 174, 89, 0.14)"
              tintInk="#e9c97a"
            />
            <StatCard
              label="Active"
              value={stats?.active ?? '—'}
              icon={<CheckCircle2 size={16} />}
              tint="rgba(115, 168, 130, 0.14)"
              tintInk="#a7d6b3"
            />
            <StatCard
              label="Suspended"
              value={stats?.suspended ?? '—'}
              icon={<Ban size={16} />}
              tint="rgba(140, 140, 140, 0.14)"
              tintInk="#bdbdbd"
            />
            <StatCard
              label="Rejected"
              value={stats?.rejected ?? '—'}
              icon={<UserX size={16} />}
              tint="rgba(201, 122, 107, 0.14)"
              tintInk="#f0c1b5"
            />
            <StatCard
              label="Clients"
              value={stats?.totalClients ?? '—'}
              icon={<Users size={16} />}
              tint="var(--accent-glow)"
              tintInk="var(--accent)"
            />
            <StatCard
              label="Sessions"
              value={stats?.totalSessions ?? '—'}
              icon={<CalendarDays size={16} />}
              tint="var(--accent-glow)"
              tintInk="var(--accent)"
            />
          </div>
        </section>

        {/* Tabs + Create user */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          marginBottom: 18, flexWrap: 'wrap',
        }}>
          <div
            style={{
              display: 'inline-flex', gap: 2, flexWrap: 'wrap',
              padding: 4, borderRadius: 12,
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border)',
            }}
          >
            {(Object.keys(TAB_LABELS) as Tab[]).map((t) => {
              const active = t === tab;
              const count = stats?.[t] ?? 0;
              return (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  style={{
                    background: active ? 'var(--bg-overlay)' : 'transparent',
                    color: active ? 'var(--ink)' : 'var(--muted)',
                    padding: '7px 14px', border: 'none', borderRadius: 8,
                    fontSize: 13, fontWeight: 500,
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    cursor: 'pointer',
                    transition: 'background 0.15s, color 0.15s',
                  }}
                >
                  {TAB_LABELS[t]}
                  <span
                    style={{
                      fontSize: 11, fontWeight: 600,
                      padding: '1px 7px', borderRadius: 999,
                      background: active ? 'var(--bg)' : 'transparent',
                      color: active ? 'var(--ink-soft)' : 'var(--muted-strong)',
                      minWidth: 18, textAlign: 'center',
                    }}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
          <div style={{ marginLeft: 'auto' }}>
            <button
              onClick={openCreate}
              className="btn-primary"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '10px 16px', fontSize: 13, minHeight: 0,
              }}
            >
              <UserPlus size={15} /> Create user
            </button>
          </div>
        </div>

        {errorMsg && (
          <div style={{
            padding: '10px 12px', marginBottom: 14,
            background: 'rgba(201, 122, 107, 0.12)',
            border: '1px solid rgba(201, 122, 107, 0.3)',
            borderRadius: 10, color: '#f5d2c8', fontSize: 13,
          }}>
            {errorMsg}
          </div>
        )}

        {/* Therapist grid */}
        {loading ? (
          <div
            className="card"
            style={{ padding: 32, display: 'flex', justifyContent: 'center' }}
          >
            <ConsiliumLoader size={48} />
          </div>
        ) : rows.length === 0 ? (
          <div
            className="card"
            style={{
              padding: '48px 24px', textAlign: 'center',
              color: 'var(--muted)',
            }}
          >
            <div
              style={{
                width: 48, height: 48, borderRadius: 999,
                background: 'var(--bg-overlay)',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: 12, color: 'var(--muted-strong)',
              }}
            >
              <Users size={22} />
            </div>
            <div style={{ fontSize: 14, color: 'var(--ink-soft)', fontWeight: 500 }}>
              No {TAB_LABELS[tab].toLowerCase()} therapists
            </div>
            <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>
              {tab === 'pending'
                ? 'Self-signups will appear here for approval.'
                : `Therapists with the ${TAB_LABELS[tab].toLowerCase()} status will appear here.`}
            </div>
          </div>
        ) : (
          <div
            style={{
              display: 'grid', gap: 14,
              gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            }}
          >
            {rows.map((t) => (
              <TherapistCard
                key={t.id}
                t={t}
                busy={busyId === t.id}
                onApprove={() => runAction(t.id, 'approve')}
                onReject={() => { setModal({ kind: 'reject', target: t }); setModalReason(''); }}
                onSuspend={() => { setModal({ kind: 'suspend', target: t }); setModalReason(''); }}
                onReactivate={() => runAction(t.id, 'reactivate')}
                onDelete={() => setModal({ kind: 'delete', target: t })}
                onEditEmail={() => openEditEmail(t)}
                onResetPassword={() => openResetPassword(t)}
              />
            ))}
          </div>
        )}
      </main>

      {createOpen && (
        <Modal
          title={createdRow ? 'User created' : 'Create user'}
          onClose={closeCreate}
        >
          {createdRow ? (
            <>
              <div style={{ fontSize: 13, color: 'var(--muted-strong)', marginBottom: 14, lineHeight: 1.5 }}>
                Share these credentials with <b>{createdRow.name}</b>. They'll be
                forced to change the password on first sign-in. This is the only
                time the temp password is shown — copy it now.
              </div>
              <div style={{
                padding: 14, borderRadius: 10,
                background: 'var(--bg-elevated)',
                border: '1px solid var(--border)',
                marginBottom: 14,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                fontSize: 13, lineHeight: 1.7,
              }}>
                <div><span style={{ color: 'var(--muted)' }}>Email: </span>{createdRow.email}</div>
                <div><span style={{ color: 'var(--muted)' }}>Temp password: </span>{createdTempPassword}</div>
              </div>
              <button
                onClick={copyCredentials}
                className="btn-ghost"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                {copied ? <Check size={14} /> : <Copy size={14} />}
                {copied ? 'Copied' : 'Copy credentials'}
              </button>
              <ModalFooter>
                <button className="btn-primary" onClick={closeCreate}>Done</button>
              </ModalFooter>
            </>
          ) : (
            <>
              <div style={{ display: 'grid', gap: 12 }}>
                <Field label="Name" required>
                  <input
                    className="input"
                    value={createName}
                    onChange={(e) => setCreateName(e.target.value)}
                    autoFocus
                  />
                </Field>
                <Field label="Email" required>
                  <input
                    className="input"
                    type="email"
                    value={createEmail}
                    onChange={(e) => setCreateEmail(e.target.value)}
                  />
                </Field>
                <Field
                  label="Temporary password"
                  required
                  hint="The user must change this on first sign-in."
                >
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      className="input"
                      value={createPassword}
                      onChange={(e) => setCreatePassword(e.target.value)}
                      style={{
                        flex: 1,
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                      }}
                    />
                    <button
                      type="button"
                      className="btn-icon"
                      onClick={async () => {
                        if (!createPassword) return;
                        try {
                          await navigator.clipboard.writeText(createPassword);
                          setCopied(true);
                          setTimeout(() => setCopied(false), 2000);
                        } catch { /* clipboard unavailable — password is visible */ }
                      }}
                      title="Copy password"
                      aria-label="Copy password"
                    >
                      {copied ? <Check size={14} /> : <Copy size={14} />}
                    </button>
                    <button
                      type="button"
                      className="btn-ghost"
                      onClick={() => setCreatePassword(generateTempPassword())}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                      title="Generate a new random password"
                    >
                      <RefreshCw size={14} /> Generate
                    </button>
                  </div>
                </Field>
              </div>

              {createError && (
                <div style={{
                  marginTop: 12, padding: '10px 12px',
                  background: 'rgba(201, 122, 107, 0.12)',
                  border: '1px solid rgba(201, 122, 107, 0.3)',
                  borderRadius: 10, color: '#f5d2c8', fontSize: 13,
                }}>
                  {createError}
                </div>
              )}

              <ModalFooter>
                <button className="btn-ghost" onClick={closeCreate} disabled={creating}>
                  Cancel
                </button>
                <button className="btn-primary" onClick={submitCreate} disabled={creating}>
                  {creating ? 'Creating…' : 'Create user'}
                </button>
              </ModalFooter>
            </>
          )}
        </Modal>
      )}

      {emailTarget && (
        <Modal
          title={`Update email for ${emailTarget.name}`}
          onClose={closeEditEmail}
        >
          <div style={{ fontSize: 13, color: 'var(--muted-strong)', marginBottom: 14, lineHeight: 1.5 }}>
            The therapist will sign in with their new email from now on.
            Their existing session stays active.
          </div>
          <Field label="Email" required>
            <input
              className="input"
              type="email"
              value={emailDraft}
              onChange={(e) => setEmailDraft(e.target.value)}
              autoFocus
            />
          </Field>
          {emailError && (
            <div style={{
              marginTop: 12, padding: '10px 12px',
              background: 'rgba(201, 122, 107, 0.12)',
              border: '1px solid rgba(201, 122, 107, 0.3)',
              borderRadius: 10, color: '#f5d2c8', fontSize: 13,
            }}>
              {emailError}
            </div>
          )}
          <ModalFooter>
            <button className="btn-ghost" onClick={closeEditEmail} disabled={emailSaving}>
              Cancel
            </button>
            <button className="btn-primary" onClick={submitEditEmail} disabled={emailSaving}>
              {emailSaving ? 'Saving…' : 'Save email'}
            </button>
          </ModalFooter>
        </Modal>
      )}

      {resetTarget && (
        <Modal
          title={resetDone ? 'Password reset' : `Reset password for ${resetTarget.name}`}
          onClose={closeResetPassword}
        >
          {resetDone ? (
            <>
              <div style={{ fontSize: 13, color: 'var(--muted-strong)', marginBottom: 14, lineHeight: 1.5 }}>
                Share these credentials with <b>{resetTarget.name}</b>. They'll be
                signed out of any active sessions and forced to change the password
                on next sign-in. This is the only time the temp password is shown —
                copy it now.
              </div>
              <div style={{
                padding: 14, borderRadius: 10,
                background: 'var(--bg-elevated)',
                border: '1px solid var(--border)',
                marginBottom: 14,
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                fontSize: 13, lineHeight: 1.7,
              }}>
                <div><span style={{ color: 'var(--muted)' }}>Email: </span>{resetTarget.email}</div>
                <div><span style={{ color: 'var(--muted)' }}>Temp password: </span>{resetPassword}</div>
              </div>
              <button
                onClick={copyResetCredentials}
                className="btn-ghost"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                {resetCopied ? <Check size={14} /> : <Copy size={14} />}
                {resetCopied ? 'Copied' : 'Copy credentials'}
              </button>
              <ModalFooter>
                <button className="btn-primary" onClick={closeResetPassword}>Done</button>
              </ModalFooter>
            </>
          ) : (
            <>
              <div style={{ fontSize: 13, color: 'var(--muted-strong)', marginBottom: 14, lineHeight: 1.5 }}>
                Sets a new temporary password. The user is signed out of any
                active sessions and must change the password on next sign-in.
              </div>
              <Field
                label="Temporary password"
                required
                hint="Share this with the user after saving."
              >
                <div style={{ display: 'flex', gap: 8 }}>
                  <input
                    className="input"
                    value={resetPassword}
                    onChange={(e) => setResetPassword(e.target.value)}
                    style={{
                      flex: 1,
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    }}
                  />
                  <button
                    type="button"
                    className="btn-ghost"
                    onClick={() => setResetPassword(generateTempPassword())}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                    title="Generate a new random password"
                  >
                    <RefreshCw size={14} /> Generate
                  </button>
                </div>
              </Field>
              {resetError && (
                <div style={{
                  marginTop: 12, padding: '10px 12px',
                  background: 'rgba(201, 122, 107, 0.12)',
                  border: '1px solid rgba(201, 122, 107, 0.3)',
                  borderRadius: 10, color: '#f5d2c8', fontSize: 13,
                }}>
                  {resetError}
                </div>
              )}
              <ModalFooter>
                <button className="btn-ghost" onClick={closeResetPassword} disabled={resetSaving}>
                  Cancel
                </button>
                <button className="btn-primary" onClick={submitResetPassword} disabled={resetSaving}>
                  {resetSaving ? 'Resetting…' : 'Reset password'}
                </button>
              </ModalFooter>
            </>
          )}
        </Modal>
      )}

      {modal && (
        <Modal
          title={
            modal.kind === 'reject' ? `Reject ${modal.target.name}?` :
            modal.kind === 'suspend' ? `Suspend ${modal.target.name}?` :
            `Delete ${modal.target.name}?`
          }
          onClose={() => { setModal(null); setModalReason(''); }}
        >
          {modal.kind === 'delete' ? (
            <div style={{ fontSize: 14, color: 'var(--muted-strong)', lineHeight: 1.6 }}>
              This permanently deletes the therapist account and ALL of their data —
              clients, packages, sessions, payments, and templates. This cannot be undone.
            </div>
          ) : (
            <>
              <div style={{ fontSize: 13, color: 'var(--muted-strong)', marginBottom: 14, lineHeight: 1.5 }}>
                {modal.kind === 'reject'
                  ? 'The user will see this reason on the login screen if they try to sign in.'
                  : 'The user will be signed out and will see this reason if they try to sign in.'}
              </div>
              <Field label="Reason (optional)">
                <textarea
                  className="input"
                  value={modalReason}
                  onChange={(e) => setModalReason(e.target.value)}
                  rows={3}
                  autoFocus
                />
              </Field>
            </>
          )}
          <ModalFooter>
            <button
              className="btn-ghost"
              onClick={() => { setModal(null); setModalReason(''); }}
              disabled={busyId === modal.target.id}
            >
              Cancel
            </button>
            <button
              className={modal.kind === 'delete' ? 'btn-danger' : 'btn-primary'}
              onClick={submitModal}
              disabled={busyId === modal.target.id}
            >
              {busyId === modal.target.id ? 'Working…' :
               modal.kind === 'delete'  ? 'Delete permanently' :
               modal.kind === 'reject'  ? 'Reject' :
               'Suspend'}
            </button>
          </ModalFooter>
        </Modal>
      )}
    </div>
  );
}

// ----------------------------------------------------------
// Helpers
// ----------------------------------------------------------

// Generate a memorable-ish temp password: 12 chars, mixed case + digits.
// Uses crypto.getRandomValues so the password is unpredictable. Avoids
// look-alike chars (0/O, 1/l/I) so admins can read it aloud over a call.
function generateTempPassword(): string {
  const ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
  const LEN = 12;
  const bytes = new Uint32Array(LEN);
  crypto.getRandomValues(bytes);
  let out = '';
  for (let i = 0; i < LEN; i++) out += ALPHABET[bytes[i] % ALPHABET.length];
  return out;
}

function StatusBadge({ status }: { status: TherapistStatus }) {
  const s = STATUS_STYLE[status];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      fontSize: 11, padding: '3px 10px 3px 8px',
      borderRadius: 999,
      background: s.bg, color: s.color,
      fontWeight: 600,
      letterSpacing: '0.01em',
    }}>
      <span
        aria-hidden
        style={{
          width: 6, height: 6, borderRadius: 999,
          background: s.dot,
          boxShadow: `0 0 0 3px ${s.bg}`,
        }}
      />
      {s.text}
    </span>
  );
}

function StatCard({
  label, value, icon, tint, tintInk,
}: {
  label: string;
  value: number | string;
  icon: React.ReactNode;
  tint: string;
  tintInk: string;
}) {
  return (
    <div className="card" style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div className="mono-cap">{label}</div>
        <div
          aria-hidden
          style={{
            width: 28, height: 28, borderRadius: 8,
            background: tint, color: tintInk,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          {icon}
        </div>
      </div>
      <div
        className="display"
        style={{
          fontSize: 30, fontWeight: 600,
          color: 'var(--ink)',
          letterSpacing: '-0.03em', lineHeight: 1,
        }}
      >
        {value}
      </div>
    </div>
  );
}

function MetricChip({ icon, value, label }: { icon: React.ReactNode; value: number; label: string }) {
  return (
    <div
      style={{
        flex: 1,
        padding: '10px 12px',
        borderRadius: 10,
        background: 'var(--bg-subtle)',
        border: '1px solid var(--border)',
        display: 'flex', alignItems: 'center', gap: 10,
        minWidth: 0,
      }}
    >
      <div
        style={{
          width: 28, height: 28, borderRadius: 8,
          background: 'var(--bg-overlay)', color: 'var(--muted)',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--ink)', lineHeight: 1.1 }}>
          {value}
        </div>
        <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{label}</div>
      </div>
    </div>
  );
}

interface TherapistCardProps {
  t: AdminTherapistListItem;
  busy: boolean;
  onApprove: () => void;
  onReject: () => void;
  onSuspend: () => void;
  onReactivate: () => void;
  onDelete: () => void;
  onEditEmail: () => void;
  onResetPassword: () => void;
}

function TherapistCard({
  t, busy, onApprove, onReject, onSuspend, onReactivate, onDelete,
  onEditEmail, onResetPassword,
}: TherapistCardProps) {
  const tint = avatarTint(t.id);

  const btn = (
    label: string,
    icon: React.ReactNode,
    kind: 'primary' | 'ghost' | 'danger',
    onClick: () => void,
  ) => (
    <button
      onClick={onClick}
      disabled={busy}
      className={kind === 'primary' ? 'btn-primary' : kind === 'danger' ? 'btn-danger' : 'btn-ghost'}
      style={{
        flex: 1,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        gap: 6, padding: '9px 12px', fontSize: 13, minHeight: 0,
      }}
    >
      {icon}{label}
    </button>
  );

  let actions: React.ReactNode;
  switch (t.status) {
    case 'pending':
      actions = <>{btn('Approve', <CheckCircle2 size={14} />, 'primary', onApprove)}{btn('Reject', <XCircle size={14} />, 'ghost', onReject)}</>;
      break;
    case 'active':
      actions = <>{btn('Suspend', <PauseCircle size={14} />, 'ghost', onSuspend)}{btn('Delete', <Trash2 size={14} />, 'danger', onDelete)}</>;
      break;
    case 'rejected':
      actions = <>{btn('Approve', <CheckCircle2 size={14} />, 'primary', onReactivate)}{btn('Delete', <Trash2 size={14} />, 'danger', onDelete)}</>;
      break;
    case 'suspended':
      actions = <>{btn('Reactivate', <PlayCircle size={14} />, 'primary', onReactivate)}{btn('Delete', <Trash2 size={14} />, 'danger', onDelete)}</>;
      break;
  }

  return (
    <div
      className="card"
      style={{
        padding: 18,
        display: 'flex', flexDirection: 'column', gap: 14,
        opacity: busy ? 0.6 : 1,
        transition: 'opacity 0.15s',
      }}
    >
      {/* Header: avatar + name/email + status */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <div
          aria-hidden
          style={{
            width: 44, height: 44, borderRadius: 12,
            background: `linear-gradient(135deg, ${tint}, ${tint}cc)`,
            color: '#1c1917',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 700, fontSize: 15, letterSpacing: '0.02em',
            flexShrink: 0,
          }}
        >
          {initials(t.name)}
        </div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div
            style={{
              fontWeight: 600, color: 'var(--ink)',
              fontSize: 15, lineHeight: 1.25,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}
            title={t.name}
          >
            {t.name}
          </div>
          <div
            style={{
              fontSize: 12.5, color: 'var(--muted)', marginTop: 3,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}
            title={t.email}
          >
            {t.email}
          </div>
          <div style={{ marginTop: 8 }}>
            <StatusBadge status={t.status} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
          <button
            className="btn-icon"
            onClick={onEditEmail}
            disabled={busy}
            title="Update email"
            aria-label="Update email"
          >
            <Mail size={15} />
          </button>
          <button
            className="btn-icon"
            onClick={onResetPassword}
            disabled={busy}
            title="Reset password"
            aria-label="Reset password"
          >
            <KeyRound size={15} />
          </button>
        </div>
      </div>

      {/* Metrics */}
      <div style={{ display: 'flex', gap: 8 }}>
        <MetricChip icon={<Users size={14} />}        value={t.clientCount}  label={t.clientCount === 1 ? 'client' : 'clients'} />
        <MetricChip icon={<Briefcase size={14} />}    value={t.sessionCount} label={t.sessionCount === 1 ? 'session' : 'sessions'} />
      </div>

      {/* Joined + optional reason */}
      <div style={{ fontSize: 11.5, color: 'var(--muted-strong)' }}>
        Joined {new Date(t.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
      </div>
      {t.rejectionReason && (
        <div
          style={{
            fontSize: 12, color: 'var(--ink-soft)',
            background: 'var(--bg-subtle)',
            border: '1px solid var(--border)',
            borderLeft: '3px solid var(--danger)',
            borderRadius: 8,
            padding: '8px 10px',
            fontStyle: 'italic',
            lineHeight: 1.45,
          }}
        >
          {t.rejectionReason}
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: 8, marginTop: 'auto' }}>
        {actions}
      </div>
    </div>
  );
}
